import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as EyeIcon } from '../../../../assets/icons/eye-fill-icon.svg';
import { useAppBranding } from '../../../../config/branding';
import DefaultApiLimit from '../../../../config/defaultLimit';
import {
  ApiRequestApprovedInterface,
  RequestApprovedInterface,
} from '../../../../interface/request-approved-interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationDetails } from '../../../../redux/slice/NotificationSlice';
import {
  disableApprovedFilterApplied,
  fetchRequestApproved,
  updateApprovedFilters,
  fetchRequestApprovedFilters,
  setSortData,
} from '../../../../redux/slice/RequestApprovedSlice';
import MyButton from '../../../../shared-components/button/Button';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import SuccessNotification from '../../../../shared-components/success-notification/SuccessNotification';
import SidePaneModel from '../../../../shared-components/table-side-panel/SidePaneModel';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../../shared-functions/ConvertUTCtoDate';
import { exportPDF, exportCSV } from '../../../../shared-functions/ExportFile';
import ToFixedValue from '../../../../shared-functions/ToFixedValue';
import penaltyManagementApi from '../../../../utils/api/penalty-management';
import AddOverrides from '../Override';

import ViewOverrides from './ViewOverrides';

interface ModelDetailsInterface {
  title: string;
  header: boolean;
  content: JSX.Element;
  rightCorner: string;
  notification: boolean;
  position?: string;
}

/**
 * Request approved component
 * @returns
 */
function RequestApproved() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('penaltyManagement');
  const branding = useAppBranding();
  const [isSidePaneOpen, setIsSidePaneOpen] = useState<boolean>(false);
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [SidePaneView, setSidePaneView] = useState<ModelDetailsInterface>({
    title: ' ',
    header: false,
    content: <div />,
    rightCorner: '',
    notification: false,
  });

  const {
    requestApproved,
    limit,
    page,
    totalPages,
    totalResults,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortBy,
    sortField,
    loading,
  } = useAppSelector((state) => state.requestApproved);

  /**
   * function for closing modal
   * @param message
   */
  const closeSidePane = (message: string) => {
    if (message === 'success') {
      setIsSidePaneOpen(false);
      setSidePaneView({
        title: ' ',
        header: false,
        content: (
          <SuccessNotification
            title={message}
            description=""
            notificationCloseTimer={3000}
            onNotificationClose={() => setIsSidePaneOpen(false)}
          />
        ),
        rightCorner: '',
        notification: false,
      });
    } else {
      setIsSidePaneOpen(false);
    }
  };

  /**
   * function for opening overlay
   */
  const handleOpenSidePane = (currentData: RequestApprovedInterface) => {
    const accountDetails = requestApproved.find((value) => value._id === currentData._id);
    if (accountDetails) {
      setIsSidePaneOpen(true);
      setSidePaneView({
        title: ' ',
        header: false,
        content: (
          <ViewOverrides currentData={accountDetails} closeModal={closeSidePane} type="approved" />
        ),
        rightCorner: '',
        notification: false,
      });
    }
  };
  /**
   * Renders custom data for the Dynamic Pricing column
   * @param {AccountListInterface} currentRow - The current row data
   * @returns {JSX.Element} The rendered dynamic pricing cell
   */
  const renderDynamicPricing = (currentRow: RequestApprovedInterface) => {
    let dynamicPricingList: string;
    if (currentRow.dynamicPricing.length > 2) {
      dynamicPricingList = t('all');
    } else {
      dynamicPricingList = currentRow.dynamicPricing
        .map((item) => item.charAt(0).toUpperCase() + item.slice(1))
        .join(', ');
    }
    return <p>{dynamicPricingList}</p>;
  };
  /**
   * Renders custom data for the Ceiling Type column
   * @param {AccountListInterface} currentRow - The current row data
   * @returns {JSX.Element} The rendered ceiling type cell
   */
  const renderCeilingType = (currentRow: RequestApprovedInterface) => {
    const formattedValue = currentRow.ceilingType
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/^./, (char) => char.toUpperCase());
    return <p>{formattedValue}</p>;
  };

  /**
   * Modal box close function
   */
  const closeSidePaneNoArguments = () => {
    setIsSidePaneOpen(false);
  };

  /**
   * AddOverrides
   */
  const handleAddOverride = () => {
    setIsSidePaneOpen(true);
    setSidePaneView({
      title: ' ',
      header: false,
      content: (
        <AddOverrides closeModal={closeSidePaneNoArguments} title={t('addOverride')} type="add" />
      ),
      rightCorner: '',
      notification: false,
    });
  };

  const headers = [
    {
      label: t('station'),
      accessorKey: 'stationNames' as keyof RequestApprovedInterface,
      sortAllow: true,
    },
    {
      label: t('dynamicPricing'),
      accessorKey: 'dynamicPricing' as keyof RequestApprovedInterface,
      sortAllow: false,
      customData: renderDynamicPricing,
    },
    {
      label: t('graceInterval'),
      accessorKey: 'graceInterval' as keyof RequestApprovedInterface,
      sortAllow: true,
    },
    {
      label: t('maxCeilingValue'),
      accessorKey: 'ceilingValue' as keyof RequestApprovedInterface,
      sortAllow: true,
      /**
       * maxCeilingValue custom function
       */
      customData: (currentRow: RequestApprovedInterface) => (
        <div className="amount-in-transaction-table">
          <p className="table-row-each-data">
            {ToFixedValue(currentRow.ceilingValue)}
          </p>
          <span>{branding.text.currency}</span>
        </div>
      ),
    },
    {
      label: t('ceilingType'),
      accessorKey: 'ceilingType' as keyof RequestApprovedInterface,
      sortAllow: false,
      customData: renderCeilingType,
    },
    {
      label: t('effectiveDate'),
      accessorKey: 'effectiveFrom' as keyof RequestApprovedInterface,
      sortAllow: true,
      /**
       * customData
       */
      customData: (customData: RequestApprovedInterface) => (
        <div>
          {ConvertUTCtoDate(customData.effectiveFrom)} - {ConvertUTCtoDate(customData.effectiveTo)}
        </div>
      ),
    },
    {
      label: t('status'),
      accessorKey: 'status' as keyof RequestApprovedInterface,
      sortAllow: true,
      /**
       * status custom function
       */
      customData: (currentRow: RequestApprovedInterface) => (
        <p className={currentRow.status ? 'active' : 'inactive'}>
          {currentRow.status ? t('active') : t('inactive')}
        </p>
      ),
    },
    {
      label: t('action'),
      accessorKey: '' as keyof RequestApprovedInterface,
      /**
       * Rendering action
       * @returns
       */
      customData: (currentData: RequestApprovedInterface) => (
        <div
          role="presentation"
          className="action-button"
          onClick={() => {
            handleOpenSidePane(currentData);
          }}>
          <EyeIcon />
        </div>
      ),
    },
  ];

  /**
   * Function for fetching request approved data
   * @param data
   */
  const fetchData = (data: ApiRequestApprovedInterface) => {
    if (!loading) {
      dispatch(fetchRequestApproved(data));
    }
  };

  /**
   * Function for refreshing request approved data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page,
      limit,
      sortBy,
      sortField,
    });
  };

  /**
   * Handle the filter data clear function
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      limit,
      page: DefaultApiLimit.page,
    });
  };

  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('station'), accessorKey: 'stationNames' },
    { label: t('dynamicPricing'), accessorKey: 'dynamicPricing' },
    { label: t('graceInterval'), accessorKey: 'graceInterval' },
    { label: t('maxCeilingValue'), accessorKey: 'ceilingValue' },
    { label: t('ceilingType'), accessorKey: 'ceilingType' },
    { label: t('effectiveDate'), accessorKey: 'effectiveDate' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Function for exporting request approved data in pdf or csv format
   * @param type
   */
  const exportDataPDFOrCSV = (type: 'pdf' | 'csv') => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    setExportLoader(true);
    penaltyManagementApi
      .requestExport({ ...queryParameters, requestStatus: 'approved' })
      .then((response) => {
        const header = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map((row: RequestApprovedInterface, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;
              case 'dynamicPricing':
                return renderDynamicPricing(row) || 'N/A';
              case 'ceilingValue':
                return `${ToFixedValue(row.ceilingValue)} ${branding.text.currency}` || 'N/A';
              case 'ceilingType':
                return renderCeilingType(row) || 'N/A';
              case 'effectiveDate':
                return (
                  `${ConvertUTCtoDate(row.effectiveFrom)} - ${ConvertUTCtoDate(row.effectiveTo)}` ||
                  'N/A'
                );
              default:
                return (row as any)[col?.accessorKey] || 'N/A';
            }
          }),
        );
        if (type === 'csv') {
          exportCSV(header, rows, 'request-approved');
        } else {
          exportPDF(header, rows, 'request-approved');
        }

        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };

  /**
   * Table pagination function
   * @param value
   */
  const pageAndLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, ...value, sortField, sortBy });
  };

  useEffect(() => {
    if (isFilterUpdated || requestApproved.length === 0) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchData({
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
      });
      dispatch(disableApprovedFilterApplied());
    }
  }, [filters]);

  return (
    <div>
      <SidePaneModel
        title={SidePaneView.title}
        // header={SidePaneView.header}
        // rightCorner={SidePaneView.rightCorner}
        content={SidePaneView.content}
        closePanel={() => setIsSidePaneOpen(false)}
        open={isSidePaneOpen}
      />
      <CustomTable
        serialNo
        paginate
        header={headers}
        data={requestApproved}
        limit={limit}
        page={page}
        totalPages={totalPages}
        totalResults={totalResults}
        filterListLoading={filterListLoading}
        filters={filters}
        currentPage={page}
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        handleLimitChange={pageAndLimitChange} // ✅ REQUIRED
        handlePageChange={pageAndLimitChange}
        exportData={requestApproved}
        exportLoader={exportLoader}
        exportDataPdfOrCSV={exportDataPDFOrCSV}
        filtersToDispatch={updateApprovedFilters}
        filterListToDispatch={fetchRequestApprovedFilters}
        refreshRequired
        sizeChangeButtonRequired
        hoverLabel={t('refresh')}
        refreshCurrentPages={handleRefresh}
        loading={loading}
        tableHeight="60vh"
        topHeader={false}
        leftTopContent={
          <div>
            <MyButton
              label={t('addOverride')}
              buttonType="submit"
              onClickFunc={handleAddOverride}
            />
          </div>
        }
        onHiddenColumnsChange={setHiddenColumns}
        flexDirection
      />
    </div>
  );
}

export default RequestApproved;
